import { useState, useEffect } from 'react';
import { Plus, Search, Database, Loader, AlertCircle, X, LayoutGrid, List } from 'lucide-react';
import type { SavedQuery, CreateQueryRequest } from '../types/query';
import { queryService } from '../services/queryService';
import QueryCard from '../components/query/QueryCard';
import QueryModal from '../components/query/QueryModal';
import QueryExecuteModal from '../components/query/QueryExecuteModal';
import QueryDeleteModal from '../components/query/QueryDeleteModal';
import ExecutionHistoryModal from '../components/query/ExecutionHistoryModal';

type ViewMode = 'grid' | 'list';

export default function QueryManager() {
  const [queries, setQueries] = useState<SavedQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const [isExecuteModalOpen, setIsExecuteModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const [selectedQuery, setSelectedQuery] = useState<SavedQuery | undefined>();
  const [queryToExecute, setQueryToExecute] = useState<SavedQuery | null>(null);
  const [queryToDelete, setQueryToDelete] = useState<SavedQuery | undefined>();
  const [queryForHistory, setQueryForHistory] = useState<SavedQuery | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadQueries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await queryService.getQueries();
      setQueries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQueries();
  }, []);

  const filteredQueries = queries.filter(query =>
    query.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    query.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    query.endpoint_slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateQuery = () => {
    setSelectedQuery(undefined);
    setIsQueryModalOpen(true);
  };

  const handleEditQuery = (query: SavedQuery) => {
    setSelectedQuery(query);
    setIsQueryModalOpen(true);
  };

  const handleDeleteQuery = (query: SavedQuery) => {
    setQueryToDelete(query);
    setIsDeleteModalOpen(true);
  };

  const handleExecuteQuery = (query: SavedQuery) => {
    setQueryToExecute(query);
    setIsExecuteModalOpen(true);
  };

  const handleToggleActive = async (query: SavedQuery) => {
    try {
      const newStatus = await queryService.toggleQueryStatus(query.id);
      setQueries(prev => prev.map(q => q.id === query.id ? { ...q, is_active: newStatus } : q));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar serviço');
    }
  };

  const handleShowHistory = (query: SavedQuery) => {
    setQueryForHistory(query);
    setIsHistoryModalOpen(true);
  };

  const handleSaveQuery = async (data: CreateQueryRequest) => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (selectedQuery) {
        const updated = await queryService.updateQuery(selectedQuery.id, data);
        setQueries(prev => prev.map(q => q.id === selectedQuery.id ? updated : q));
      } else {
        const created = await queryService.createQuery(data);
        setQueries(prev => [created, ...prev]);
      }

      setIsQueryModalOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar serviço';
      setError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!queryToDelete) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await queryService.deleteQuery(queryToDelete.id);
      setQueries(prev => prev.filter(q => q.id !== queryToDelete.id));
      setIsDeleteModalOpen(false);
      setQueryToDelete(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir serviço');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando serviços...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gerenciador de Serviços</h1>
                <p className="text-gray-600">Crie, execute e gerencie seus serviços SQL</p>
              </div>
            </div>
            <button
              onClick={handleCreateQuery}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Novo Serviço</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar serviços por nome, descrição ou endpoint..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
                title="Visualizacao em grade"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
                title="Visualizacao em lista"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-gray-500">
              {filteredQueries.length} serviço{filteredQueries.length !== 1 ? 's' : ''} encontrado{filteredQueries.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {filteredQueries.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nenhum serviço encontrado' : 'Nenhum serviço cadastrado'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm
                ? 'Tente ajustar os filtros de busca'
                : 'Comece criando seu primeiro serviço'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={handleCreateQuery}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Criar Primeiro Serviço</span>
              </button>
            )}
          </div>
        ) : (
          <div className={viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
            : 'space-y-4'
          }>
            {filteredQueries.map((query) => (
              <QueryCard
                key={query.id}
                query={query}
                onEdit={handleEditQuery}
                onDelete={handleDeleteQuery}
                onExecute={handleExecuteQuery}
                onToggleActive={handleToggleActive}
                onShowHistory={handleShowHistory}
              />
            ))}
          </div>
        )}
      </div>

      <QueryModal
        isOpen={isQueryModalOpen}
        onClose={() => setIsQueryModalOpen(false)}
        onSave={handleSaveQuery}
        query={selectedQuery}
        isSubmitting={isSubmitting}
      />

      <QueryExecuteModal
        isOpen={isExecuteModalOpen}
        onClose={() => {
          setIsExecuteModalOpen(false);
          setQueryToExecute(null);
        }}
        query={queryToExecute}
      />

      <QueryDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setQueryToDelete(undefined);
        }}
        onConfirm={confirmDelete}
        queryName={queryToDelete?.name || ''}
        isSubmitting={isSubmitting}
      />

      {isHistoryModalOpen && queryForHistory && (
        <ExecutionHistoryModal
          query={queryForHistory}
          onClose={() => {
            setIsHistoryModalOpen(false);
            setQueryForHistory(null);
          }}
        />
      )}
    </div>
  );
}
